<template>
  <SafeTeleport to="#status-dropdown-target">
    <div>
      <Dropdown
        :items="statusOptions"
        :label="label"
        @update:modelValue="handleStatusSelect"
      />
      <Modal
        v-if="modalOpen"
        title="Appointment Status Change"
        @hide-modal="hideModal"
        @submit="handleSave"
      >
        <div>
          <Alert type="warning" class="mb-3">
            <div v-if="lostFields.length">
              By changing the status to "<span class="font-bold">{{
                selectedStatus
              }}</span
              >" the following times will be reset.
            </div>
            <div v-else-if="requiredFields.length">
              By changing the status to "<span class="font-bold">{{
                selectedStatus
              }}</span
              >" you will need to update the times below to reflect the correct
              time.
            </div>
          </Alert>
          <AppointmentTimeline
            :appointment="appointment"
            :editable-times="requiredFields"
            :lost-times="lostFields"
            @update:modelValue="handleUpdateTime"
          />
          <TextArea
            v-model="reason"
            class="mt-4"
            message="Reason for status change"
          />
        </div>
      </Modal>
    </div>
  </SafeTeleport>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Dropdown from "@/components/Seranova/Dropdown/Dropdown.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import CONSTANTS from "@/Constants";
import { AppointmentStatus, AppointmentV2 } from "@/typings/appointment";
const { APPOINTMENT_STATUS_ORDER, APPOINTMENT_STATUS_MAP } = CONSTANTS;
import { SafeTeleport } from "vue-safe-teleport";

import AppointmentTimeline from "./AppointmentTimeline.vue";
import { useAppointmentStatus } from "./useAppointmentStatus";

const emit = defineEmits(["refresh-appointment"]);
const props = defineProps<{ appointment: AppointmentV2 }>();
const { getRequiredFieldsForStatus, times, changeStatus, getTimesToRemove } =
  useAppointmentStatus(props.appointment);
const selectedStatus = ref<AppointmentStatus | null>(null);
const modalOpen = ref(false);
const reason = ref("");
const statusOptions = computed(() => {
  const availableStatuses = [props.appointment.status].concat(
    Object.keys(props.appointment.links)
  );
  // Sort statuses by flow
  const sortedStatuses = APPOINTMENT_STATUS_ORDER.filter((status) =>
    availableStatuses.includes(status)
  );
  return sortedStatuses
    .filter(
      (status) =>
        status !== props.appointment.status &&
        APPOINTMENT_STATUS_MAP[status] &&
        props.appointment.links[status]
    )
    .map((status) => ({
      value: status,
      label: APPOINTMENT_STATUS_MAP[status]
    }));
});
const requiredFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getRequiredFieldsForStatus(selectedStatus.value);
});
const lostFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getTimesToRemove(props.appointment.status, selectedStatus.value);
});

const label = computed(() => APPOINTMENT_STATUS_MAP[props.appointment.status]);
const handleStatusSelect = (item: { value: string; label: string }) => {
  selectedStatus.value = item.value as AppointmentStatus;
  modalOpen.value = true;
};
const hideModal = () => {
  modalOpen.value = false;
};

const handleSave = async () => {
  if (!selectedStatus.value) return;
  await changeStatus(
    selectedStatus.value,
    times,
    reason.value,
    props.appointment.links[selectedStatus.value]
  );
  modalOpen.value = false;
  window.notify.success("Appointment status updated");
  emit("refresh-appointment");
};
const handleUpdateTime = (data: { [k: string]: string }) => {
  const fieldName = Object.keys(data)[0];
  times[fieldName] = data[fieldName];
};
</script>

<style scoped></style>
